import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBold,
  faItalic,
  faList,
  faListOl,
  faRotateLeft,
  faRotateRight,
  faUnderline,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AllSelection } from "@tiptap/pm/state";
import { Editor, useEditorState } from "@tiptap/react";
import React from "react";

/**
 * Workaround for TipTap bug where toggling a list with AllSelection
 * (Cmd+A) adds empty items instead of untoggling. AllSelection includes
 * a trailing empty paragraph that confuses the toggle command.
 * Remove once https://github.com/ueberdosis/tiptap/pull/7455 is released.
 */
function toggleListSafe(
  editor: Editor,
  listType: "bulletList" | "orderedList",
) {
  const { selection, doc } = editor.state;

  if (
    selection instanceof AllSelection &&
    (doc.firstChild?.type.name === "bulletList" ||
      doc.firstChild?.type.name === "orderedList") &&
    doc.childCount === 2 &&
    doc.lastChild?.type.name === "paragraph" &&
    doc.lastChild.content.size === 0
  ) {
    const firstChild = doc.firstChild;
    const listEnd = firstChild.nodeSize;

    // Find exact text positions inside the list via nodesBetween
    let firstTextPos = -1;
    let lastTextPos = -1;

    doc.nodesBetween(1, listEnd, (node, pos) => {
      if (node.isTextblock) {
        if (firstTextPos === -1) firstTextPos = pos + 1;
        lastTextPos = pos + node.nodeSize - 1;
      }
    });

    if (firstTextPos !== -1) {
      const chain = editor
        .chain()
        .focus()
        .setTextSelection({ from: firstTextPos, to: lastTextPos });

      if (listType === "bulletList") {
        chain.toggleBulletList().run();
      } else {
        chain.toggleOrderedList().run();
      }
      return;
    }
  }

  if (listType === "bulletList") {
    editor.chain().focus().toggleBulletList().run();
  } else {
    editor.chain().focus().toggleOrderedList().run();
  }
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  icon: IconProp;
  label: string;
}

const ToolbarButton = ({
  onClick,
  isActive = false,
  disabled = false,
  icon,
  label,
}: ToolbarButtonProps) => {
  const baseStyles =
    "flex items-center justify-center w-8 h-8 rounded-full transition-colors text-sm";
  const activeStyles = isActive
    ? "bg-brand text-white"
    : "bg-transparent text-secondary hover:bg-brand-subtle";
  const disabledStyles = disabled
    ? "opacity-50 cursor-not-allowed"
    : "cursor-pointer";

  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      className={`${baseStyles} ${activeStyles} ${disabledStyles}`}
      title={label}
      aria-label={label}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  );
};

export interface DSRichTextToolbarProps {
  /** Tiptap editor instance - when provided, shows formatting buttons */
  editor?: Editor | null;
  disabled?: boolean;
  textareaTitle?: string;
  /** Optional background class for the toolbar (overrides default bg-secondary) */
  bgClass?: string;
  /** Optional border class for the toolbar (overrides default border-secondary) */
  borderClass?: string;
  /** Optional content to render on the right side of the toolbar */
  children?: React.ReactNode;
  /** Test ID for automated testing */
  dataTestId: string;
  /** Hide bold, italic, and list formatting buttons */
  hideFormatting?: boolean;
}

export const DSRichTextToolbar = ({
  editor,
  disabled = false,
  textareaTitle = "",
  bgClass,
  borderClass,
  children,
  dataTestId,
  hideFormatting = false,
}: DSRichTextToolbarProps) => {
  // Subscribe to editor state changes so active marks/nodes update reactively.
  // In Tiptap v3, useEditor no longer re-renders on every transaction.
  const editorState = useEditorState({
    editor: editor ?? null,
    selector: (ctx) => {
      if (!ctx.editor) return null;
      return {
        isFocused: ctx.editor.isFocused,
        bold: ctx.editor.isActive("bold"),
        italic: ctx.editor.isActive("italic"),
        underline: ctx.editor.isActive("underline"),
        bulletList: ctx.editor.isActive("bulletList"),
        orderedList: ctx.editor.isActive("orderedList"),
      };
    },
  });

  const notFocused = !editorState?.isFocused;
  const customBgClass = bgClass || "bg-secondary";
  const customBorderClass = borderClass || "border-secondary";

  return (
    <div
      className={`flex items-center gap-xs p-sm border ${customBorderClass} ${customBgClass} rounded-standard`}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
    >
      {/* Textarea title */}
      {textareaTitle && (
        <div className="mx-sm">
          <h4>{textareaTitle}</h4>
        </div>
      )}

      {/* Text Formatting - only shown when editor is provided and not in plain text mode */}
      {editor && !hideFormatting && (
        <>
          <div className="flex items-center gap-xs border-r border-secondary pr-sm">
            <ToolbarButton
              onClick={() =>
                editor.chain().focus().toggleBold().run()
              }
              isActive={editorState?.bold}
              disabled={disabled || notFocused}
              icon={faBold}
              label="Bold (Cmd+B)"
            />
            <ToolbarButton
              onClick={() =>
                editor.chain().focus().toggleItalic().run()
              }
              isActive={editorState?.italic}
              disabled={disabled || notFocused}
              icon={faItalic}
              label="Italic (Cmd+I)"
            />
            <ToolbarButton
              onClick={() =>
                editor.chain().focus().toggleUnderline().run()
              }
              isActive={editorState?.underline}
              disabled={disabled || notFocused}
              icon={faUnderline}
              label="Underline (Cmd+U)"
            />
          </div>

          {/* Lists */}
          <div className="flex items-center gap-xs border-r border-secondary pr-sm">
            <ToolbarButton
              onClick={() => toggleListSafe(editor, "bulletList")}
              isActive={editorState?.bulletList}
              disabled={disabled || notFocused}
              icon={faList}
              label="Bullet List"
            />
            <ToolbarButton
              onClick={() => toggleListSafe(editor, "orderedList")}
              isActive={editorState?.orderedList}
              disabled={disabled || notFocused}
              icon={faListOl}
              label="Numbered List"
            />
          </div>
        </>
      )}

      {/* Undo/Redo - always shown when editor is provided */}
      {editor && (
        <>
          <div className="flex items-center gap-xs">
            <ToolbarButton
              onClick={() => editor.chain().focus().undo().run()}
              disabled={disabled}
              icon={faRotateLeft}
              label="Undo (Cmd+Z)"
            />
            <ToolbarButton
              onClick={() => editor.chain().focus().redo().run()}
              disabled={disabled}
              icon={faRotateRight}
              label="Redo (Cmd+Shift+Z)"
            />
          </div>
        </>
      )}

      {/* Custom content (e.g., Accept/Reject buttons) */}
      {children && (
        <div className="ml-auto flex items-center">{children}</div>
      )}
    </div>
  );
};
